import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'framer-motion';

/**
 * FounderStorySection — centered eyebrow + headline above a single
 * dark founder card with portrait, story, signature, and "Read the
 * full story" CTA. Sits immediately after the industries panels.
 */
export function FounderStorySection() {
  return (
    <section
      id='founder'
      className='relative py-14 md:py-20 overflow-hidden bg-zinc-950/60'
    >
      {/* Subtle dark tint overlay — sits between the global ThreeScene
          tree and the content so the founder card has its own grounded
          atmosphere without losing the ambient scene behind it. */}
      <div
        aria-hidden='true'
        className='absolute inset-0 bg-gradient-to-b from-zinc-950/40 via-zinc-950/30 to-zinc-950/50 pointer-events-none'
      />
      <div className='tc-container relative z-10'>
        {/* Eyebrow + Headline */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className='text-center max-w-3xl mx-auto mb-12 md:mb-16'
        >
          <span className='inline-block px-4 py-1.5 rounded-full text-[11px] font-sans tracking-[0.25em] uppercase border bg-jade-deep/20 text-jade-soft border-jade-deep/60 mb-5'>
            Founder Story
          </span>
          <h2 className='text-4xl sm:text-5xl md:text-6xl font-display font-extrabold leading-[1.05] text-white [text-wrap:balance]'>
            Built from a{' '}
            <span className='text-jade-soft'>real shop floor.</span>
          </h2>
        </motion.div>

        {/* Founder card */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className='max-w-5xl mx-auto p-8 md:p-12 rounded-2xl bg-zinc-950/80 backdrop-blur-md border border-white/10'
        >
          <div className='grid md:grid-cols-[220px,1fr] gap-8 md:gap-10 items-start'>
            {/* Portrait + name */}
            <div className='text-center md:text-left'>
              <div className='relative w-40 h-40 md:w-48 md:h-48 mx-auto md:mx-0 rounded-xl overflow-hidden border border-white/10 mb-4'>
                <Image
                  src='/images/justin-founder.png'
                  alt='Justin Romanos, Founder of TimberCloud'
                  fill
                  sizes='(min-width: 768px) 192px, 160px'
                  className='object-cover'
                />
              </div>
              <div className='text-xl font-bold text-white'>Justin Romanos</div>
              <div className='text-sm text-zinc-400'>Founder, TimberCloud</div>
            </div>

            {/* Story */}
            <div className='space-y-4 text-base md:text-lg leading-relaxed text-zinc-200'>
              <p>
                I spent more than a decade at my family’s business, Porta Door,
                a cabinet door and architectural millwork manufacturer started
                by my grandfather in 1958 and later carried forward by my
                father. During that time, I saw firsthand how difficult it was
                for custom shops to modernize with software that wasn’t built
                for the way they actually work.
              </p>
              <p>
                Shopify wasn&rsquo;t built for custom sizes, options, pricing
                logic, and production. ERPs were expensive, slow, and
                back-office-first. Shops were stuck forcing their work into
                tools that were never made for them.
              </p>
              <p>
                So I built TimberCloud &mdash; configurable commerce on the
                front end, connected order and production workflows on the back
                end. One system built around the way custom shops actually sell,
                build, and fulfill orders.
              </p>
              <div className='pt-2 border-t border-white/10 mt-6'>
                <p
                  className='text-4xl md:text-5xl text-jade-soft mt-3 leading-tight'
                  style={{ fontFamily: '"Ephesis", cursive' }}
                >
                  Justin Romanos
                </p>
                <p className='text-sm text-zinc-400 mt-1'>
                  &mdash; Justin Romanos, Founder
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className='flex justify-center mt-10'
        >
          {/* Tertiary action — animated-underline text link rather than
              a solid jade pill, since the page already uses pill CTAs
              for primary conversion ("Book a Demo"). Visual hierarchy:
              pill = primary, outline = secondary, underline text =
              tertiary. */}
          <Link
            href='/about'
            className='group inline-flex items-center gap-2 text-jade-soft hover:text-jade font-semibold text-base transition-colors duration-200'
          >
            <span className='relative'>
              Read the Full Story
              <span
                aria-hidden='true'
                className='absolute left-0 -bottom-0.5 h-px w-full origin-left scale-x-0 bg-gradient-to-r from-jade-soft via-jade to-jade-soft transition-transform duration-300 ease-out group-hover:scale-x-100'
              />
            </span>
            <svg
              className='h-4 w-4 transition-all duration-200 group-hover:translate-x-1 group-hover:drop-shadow-[0_0_8px_rgba(52,211,153,0.7)]'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
              strokeWidth={2}
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M14 5l7 7m0 0l-7 7m7-7H3'
              />
            </svg>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

export default FounderStorySection;
